import { useMemo } from "react";
import { OrganizationZapFolderId, TrashIndexSort } from "@zapier/bff";
import { FOLDER_ASSETS_PAGE_SIZE } from "@zapier/utils";
import { useOrganizationZapsPagination } from "../zap/useOrganizationZapsPagination";
import { useQueryFolderAssets } from "./useQueryFolderAssets";
import { hydrateMemberFromLookup } from "../member/deactivatedMemberOwner";
import { useMembers } from "../member/useMembers";

// Reexport these so that they can be referenced from @zapier/client
export type {
  FolderAsset,
  HydratedAsset,
  HydratedZapAsset,
  HydratedInterfaceAsset,
  HydratedCanvasAsset,
  HydratedTableAsset,
  HydratedVirtualTableAsset,
  HydratedTrashedTableViewAsset,
  HydratedAgentAsset,
  HydratedChatbotAsset,
  HydratedNextGenZapAsset,
  HydratedFallbackAsset,
} from "@zapier/bff";

/**
 * Extracts the owner ID from an asset based on different asset type patterns.
 * Different asset types store owner information in different properties.
 *
 * @param asset - The asset object to extract owner ID from
 * @returns The owner ID as a number, or undefined if no owner ID is found
 */
export function extractAssetOwnerId(asset: any): number | undefined {
  const ownerId =
    asset.customuserId ?? // interface, zap
    (asset.ownerId ? Number(asset.ownerId) : undefined) ?? // canvas
    asset.ownerCustomuserId ?? // chatbots, agent
    asset.ownerZapierCustomuserId ?? // tables
    (asset.ownerUserId ? Number(asset.ownerUserId) : undefined); // next gen zap

  return ownerId;
}

interface Input {
  enabled?: boolean;
  folderId?: OrganizationZapFolderId;
  ownerId?: number;
  page?: number;
  pageSize?: number;
  subfoldersOffset?: number; // allow the first page of assets to be shorter depending on subfolder length
  /** Server-side search and sort. Honoured by the trash index path only. */
  search?: string;
  sort?: TrashIndexSort;
}

export function useFolderAssets({
  enabled = true,
  folderId,
  ownerId,
  page = 1,
  pageSize = FOLDER_ASSETS_PAGE_SIZE,
  subfoldersOffset = 0,
  search,
  sort,
}: Input) {
  // Will rename this later - it isn't specific to zaps
  const { limit, offset } = useOrganizationZapsPagination({
    page,
    pageSize,
    subfoldersOffset,
  });

  const {
    response: folderAssets,
    loadError,
    failedAssetTypes,
    isLoading,
    isError,
    hideAssets,
    errorStatus,
  } = useQueryFolderAssets({
    enabled,
    folderId,
    limit,
    offset,
    ownerId,
    search,
    sort,
  });

  const dehydratedAssets = useMemo(
    () => (hideAssets ? [] : folderAssets?.results || []),
    [folderAssets, hideAssets],
  );

  // Find all owner IDs for assets
  const memberIds: number[] = useMemo(() => {
    const ids = dehydratedAssets
      .map((asset: any) => extractAssetOwnerId(asset))
      .filter((id): id is number => id !== undefined && Number.isFinite(id));
    return ids;
  }, [dehydratedAssets]);

  // Fetch all members for the owner IDs
  const { members, isPending: isMembersPending = false } = useMembers({
    ids: memberIds,
  });

  // Map the assets to include the owner member
  const assets = useMemo(() => {
    return dehydratedAssets.map((asset) => {
      const ownerId = extractAssetOwnerId(asset);

      return {
        ...asset,
        owner: ownerId
          ? hydrateMemberFromLookup(members, ownerId, {
              deferDeactivatedUntilMembersResolve: true,
              isMembersPending,
            })
          : undefined,
      };
    });
  }, [dehydratedAssets, members, isMembersPending]);

  const totalResults = (folderAssets?.meta?.count || 0) + subfoldersOffset;
  const totalPages = Math.ceil(totalResults / pageSize) || 1;

  return {
    loadError,
    failedAssetTypes,
    errorStatus,
    isError,
    meta: useMemo(() => folderAssets?.meta, [folderAssets]),
    totalPages,
    totalCount: useMemo(
      () => (folderAssets?.meta?.count || 0) + subfoldersOffset,
      [folderAssets, subfoldersOffset],
    ),
    totalResults,
    assets,
    isLoading,
  };
}
